import React, { useState } from 'react';
import { ArrowRight, Phone, MapPin, Truck as TruckIcon, User, Calendar, IndianRupee, Package, GitBranch, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { myLoads } from '../mock/mockData';
import { toast } from '../hooks/use-toast';
import TrackingMap from '../components/TrackingMap';
import TripTimeline from '../components/TripTimeline';
import LoadChat from '../components/LoadChat';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const statusColor = (s) => {
  if (s === 'In Transit') return 'bg-[#00A651]/10 text-[#00A651]';
  if (s === 'Delivered') return 'bg-blue-50 text-blue-600';
  if (s === 'Loading') return 'bg-[#FFB800]/15 text-[#B37800]';
  return 'bg-gray-100 text-gray-600';
};

const LoadRow = ({ l, onTrack, onTimeline, onChat }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 hover-lift">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-xs text-gray-500">{l.id}</span>
          <Badge className={`border-0 text-xs ${statusColor(l.status)}`}>{l.status}</Badge>
          <Badge className="bg-gray-100 text-gray-700 border-0 text-xs">{l.paymentStatus}</Badge>
        </div>
        <div className="flex items-center gap-2 text-lg font-bold text-[#0B2545]">
          <MapPin className="w-4 h-4 text-[#00A651]" />
          <span>{l.origin.split(',')[0]}</span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span>{l.destination.split(',')[0]}</span>
        </div>
      </div>
      <p className="text-2xl font-extrabold text-[#00A651] font-[Poppins]">{formatINR(l.rate)}</p>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
      <div><p className="text-xs text-gray-500">Material</p><p className="font-semibold text-[#0B2545]">{l.material}</p></div>
      <div><p className="text-xs text-gray-500">Weight</p><p className="font-semibold text-[#0B2545]">{l.weight}</p></div>
      <div><p className="text-xs text-gray-500">Truck</p><p className="font-semibold text-[#0B2545]">{l.truckType}</p></div>
      <div><p className="text-xs text-gray-500">Vehicle</p><p className="font-semibold text-[#0B2545] font-mono text-xs">{l.vehicleNumber}</p></div>
    </div>

    {l.progress > 0 && l.progress < 100 && (
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span><span>{l.progress}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full brand-gradient" style={{ width: `${l.progress}%` }}></div>
        </div>
      </div>
    )}

    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
      <div className="flex items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{l.driver}</div>
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Pickup: {l.pickupDate}</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => toast({ title: 'Calling driver...', description: l.driverPhone })}><Phone className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => onChat(l)}><MessageCircle className="w-4 h-4 mr-1" />Chat</Button>
        <Button size="sm" variant="outline" onClick={() => onTimeline(l)}><GitBranch className="w-4 h-4 mr-1" />Timeline</Button>
        <Button size="sm" className="bg-[#0B2545] hover:bg-[#13315C] text-white" onClick={() => onTrack(l)}>Track Live</Button>
      </div>
    </div>
  </div>
);

export default function MyLoads() {
  const [tab, setTab] = useState('all');
  const [trackLoad, setTrackLoad] = useState(null);
  const [timelineLoad, setTimelineLoad] = useState(null);
  const [chatLoad, setChatLoad] = useState(null);
  const filter = (s) => s === 'all' ? myLoads : myLoads.filter(l => l.status.toLowerCase().replace(' ', '') === s);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">My Loads</h1>
        <p className="text-sm text-gray-500 mt-1">Track and manage all your active shipments.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-100">
          <TabsTrigger value="all">All ({myLoads.length})</TabsTrigger>
          <TabsTrigger value="intransit">In Transit</TabsTrigger>
          <TabsTrigger value="loading">Loading</TabsTrigger>
          <TabsTrigger value="delivered">Delivered</TabsTrigger>
          <TabsTrigger value="assigned">Assigned</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-3">
          {filter(tab).map(l => <LoadRow key={l.id} l={l} onTrack={setTrackLoad} onTimeline={setTimelineLoad} onChat={setChatLoad} />)}
          {filter(tab).length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No loads in this category.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TrackingMap open={!!trackLoad} onClose={() => setTrackLoad(null)} load={trackLoad} />
      <TripTimeline open={!!timelineLoad} onClose={() => setTimelineLoad(null)} load={timelineLoad} />
      <LoadChat open={!!chatLoad} onClose={() => setChatLoad(null)} load={chatLoad} />
    </div>
  );
}
